import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, Loader2, Download, Truck, Mail, Package } from "lucide-react";

/**
 * Guest order access page.
 *
 * Anyone holding the `guest_token` (a UUID minted at checkout time) can view
 * their receipt, digital download, or shipping info here — no account needed.
 * Bookmark-friendly URL: `/order/access/:token`.
 */
export default function GuestOrderAccess() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/orders/guest/${token}`);
        if (!alive) return;
        setO(data);
        // If payment hasn't caught up yet, poll a few times
        if (data.payment_status !== "paid") {
          setTimeout(tick, 2500);
        }
      } catch (e) {
        if (alive) setErr(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    tick();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="max-w-xl mx-auto p-20 text-center" data-testid="guest-order-loading">
        <Loader2 size={32} className="mx-auto animate-spin"/>
        <p className="mt-4 text-[#8A8A8A]">Loading your order…</p>
      </div>
    );
  }

  if (err || !o) {
    return (
      <div className="max-w-xl mx-auto p-16 text-center" data-testid="guest-order-error">
        <h1 className="h-display text-3xl text-[#B84B4B]">Order not found</h1>
        <p className="text-[#4A4A4A] mt-2">{err || "Check the link in your email and try again."}</p>
        <Link to="/" className="btn-ghost mt-6 inline-flex">Back to Hoomanz</Link>
      </div>
    );
  }

  const isPhysical = o.product_type === "physical";
  const paid = o.payment_status === "paid";

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="guest-order-page">
      <div className="text-center">
        {paid
          ? <CheckCircle2 size={44} className="mx-auto text-[#0A0A0A]"/>
          : <Loader2 size={32} className="mx-auto animate-spin text-[#8A8A8A]"/>}
        <p className="overline mt-4">{paid ? "Purchase confirmed" : "Payment processing"}</p>
        <h1 className="h-display text-3xl mt-1">{o.product_title}</h1>
        <p className="text-[#4A4A4A] mt-2">
          {o.currency} ${Number(o.amount).toFixed(2)}
          {o.buyer_email && <> · sent to <b>{o.buyer_email}</b></>}
        </p>
      </div>

      <div className="card p-6 mt-8" data-testid="guest-order-summary">
        {o.product_image && (
          <div className="rounded-2xl overflow-hidden bg-[#F2F2F0] aspect-[16/9] mb-5">
            <img src={o.product_image} alt="" className="w-full h-full object-cover"/>
          </div>
        )}

        <p className="text-xs uppercase tracking-wider text-[#8A8A8A] mb-2">Order summary</p>
        <Row label="Subtotal" value={o.subtotal}/>
        {o.shipping > 0 && <Row label="Shipping" value={o.shipping}/>}
        {o.tax > 0 && <Row label="Tax" value={o.tax}/>}
        <div className="border-t border-[#E5E5E3] mt-2 pt-2 flex items-baseline justify-between">
          <span className="font-medium">Total paid</span>
          <span className="font-heading text-2xl" data-testid="guest-order-total">${Number(o.amount).toFixed(2)}</span>
        </div>

        {paid && !isPhysical && o.download_url && (
          <a href={o.download_url} target="_blank" rel="noreferrer"
             className="btn-primary w-full mt-6"
             data-testid="guest-order-download">
            <Download size={16}/> Download your file
          </a>
        )}
        {paid && !isPhysical && !o.download_url && (
          <p className="text-sm text-[#8A8A8A] mt-6 text-center">
            The seller hasn&apos;t uploaded a downloadable file for this product yet. Check your email — they may send it directly.
          </p>
        )}
        {paid && isPhysical && (
          <div className="mt-6 rounded-2xl bg-[#F8F8F6] p-4 text-sm" data-testid="guest-order-shipping">
            <p className="inline-flex items-center gap-2 font-medium mb-1"><Truck size={13}/> Shipping details</p>
            <p className="text-[#4A4A4A]">
              {o.ship_by
                ? <>The seller is preparing your order and will ship it by <b>{new Date(o.ship_by).toLocaleDateString()}</b>. You&apos;ll receive tracking on your email once it&apos;s sent.</>
                : <>Your seller will ship this item soon and email you tracking details.</>}
            </p>
          </div>
        )}
        {!paid && (
          <p className="text-sm text-[#8A8A8A] mt-6 text-center">
            We&apos;re still confirming your payment with Stripe. This usually takes a few seconds.
          </p>
        )}
      </div>

      {o.seller && (
        <div className="card p-5 mt-4 flex items-center gap-4" data-testid="guest-order-seller">
          {o.seller.avatar && <img src={o.seller.avatar} alt="" className="h-11 w-11 rounded-full object-cover bg-[#E5E5E3]"/>}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Seller</p>
            <p className="font-medium truncate">{o.seller.name}</p>
          </div>
          <Link to={`/u/${o.seller.id}`} className="btn-ghost text-xs">View profile</Link>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-[#F8F8F6] p-4 text-sm text-[#4A4A4A]" data-testid="guest-order-tips">
        <p className="font-medium text-[#0A0A0A] mb-1"><Mail size={13} className="inline mr-1"/> Keep this link safe.</p>
        <p>
          Bookmark this page — anyone with this link can view your receipt and download.
          If you lose it, check the email we sent to <b>{o.buyer_email || "your inbox"}</b>.
          Want to keep track of all your purchases in one place? <Link to="/register" className="underline text-[#0057FF]">Create a free Hoomanz account</Link>.
        </p>
      </div>

      <div className="text-center mt-8">
        <Link to="/shop" className="btn-ghost inline-flex text-sm" data-testid="guest-order-continue">
          <Package size={13}/> Browse more on Hoomanz
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#4A4A4A]">{label}</span>
      <span>${Number(value || 0).toFixed(2)}</span>
    </div>
  );
}
